import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarCheck, ShieldCheck, Sparkles, X,
  Stethoscope, Users, Plug,
  Phone, Inbox, BookOpen, BarChart3, Megaphone, Star,
  DollarSign, Unlock,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { FeatureSpotlight } from "@/components/landing/FeatureSpotlight";
import {
  CallIQVisual, AskKaiVisual, ClinicPulseVisual, ReactivationVisual,
  ChannelHub,
} from "@/components/landing/SpotlightVisuals";

/* Consistent kicker style */
const KICKER = "font-display text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground";

/* Tools Clinic OS replaces — shown as 6 named chips in a bigger callout. */
const REPLACED_TOOLS = [
  { name: "CallHero",  domain: "callhero.ca", brand: { letters: "CH", from: "from-orange-500", to: "to-rose-500" } }, // custom brand chip
  { name: "Mailchimp", domain: "mailchimp.com" },
  { name: "Hootsuite", domain: "hootsuite.com" },
  { name: "Intercom",  domain: "intercom.com" },
  { name: "ManyChat",  domain: "manychat.com" },
  { name: "Zapier",    domain: "zapier.com" },
];

const ReplaceChip = ({ t, i }) => {
  const Brand = t.brand ? (
    <span
      className={`flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br ${t.brand.from} ${t.brand.to} font-display text-[10px] font-black leading-none text-white shadow-sm ring-1 ring-inset ring-white/15`}
      aria-hidden="true"
    >
      {t.brand.letters}
    </span>
  ) : (
    <img
      src={`https://logo.clearbit.com/${t.domain}`}
      alt={t.name}
      loading="lazy"
      className="h-5 w-5 rounded object-contain"
      onError={(e) => {
        const fb = `https://www.google.com/s2/favicons?domain=${t.domain}&sz=256`;
        if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
      }}
    />
  );
  return (
    <motion.span
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.6 + i * 0.04 }}
      data-testid={`replace-chip-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
      title={t.name}
      className="group relative inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 shadow-sm transition-all hover:-translate-y-0.5 hover:border-rose-400/55 hover:shadow-md"
    >
      {Brand}
      <span className="font-display text-[12.5px] font-black tracking-tight text-foreground/90">
        {t.name}
      </span>
      <X
        className="pointer-events-none absolute -right-1.5 -top-1.5 h-3.5 w-3.5 rounded-full bg-rose-500 p-0.5 text-white shadow-sm"
        strokeWidth={3.5}
      />
    </motion.span>
  );
};

/* Small integration-logo badge — links to its /integrations/:slug page */
const IntegrationLogo = ({ domain, label, to }) => {
  const src = `https://logo.clearbit.com/${domain}`;
  const fallback = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return (
    <Link
      to={to}
      data-testid={`hero-integration-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/35 hover:shadow"
    >
      <img
        src={src}
        alt={label}
        loading="lazy"
        className="h-4 w-4 object-contain"
        onError={(e) => { if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback; }}
      />
      <span className="font-display text-[11.5px] font-black tracking-tight text-foreground/90 group-hover:text-foreground">{label}</span>
      <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-0.5" />
    </Link>
  );
};

/* Big EMR partner card — the hero "Live in" proof block */
const EmrPartnerCard = ({ domain, label, to }) => {
  const src = `https://logo.clearbit.com/${domain}`;
  const fallback = `https://www.google.com/s2/favicons?domain=${domain}&sz=256`;
  return (
    <Link
      to={to}
      data-testid={`hero-emr-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/35 hover:shadow-md sm:gap-3.5 sm:px-4 sm:py-3"
    >
      <span className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-500/[0.08] blur-3xl opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-border bg-secondary/40 sm:h-10 sm:w-10">
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-6 w-6 object-contain sm:h-7 sm:w-7"
          onError={(e) => { if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback; }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[14.5px] font-black tracking-[-0.01em] text-foreground sm:text-[15.5px]">
          {label}
        </p>
        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Native 2-way sync
        </p>
      </div>
      <ArrowRight className="h-3.5 w-3.5 flex-none text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Link>
  );
};

/* ────────────────────────────────────────────────────────────────
   Hero — plain English, who/what/why, with specialty self-select.
   ──────────────────────────────────────────────────────────────── */
const Hero = () => {
  const openDemo = useDemo();
  return (
    <section
      data-testid="home-hero"
      className="relative isolate overflow-hidden pt-24 pb-12 sm:min-h-[calc(100vh-4rem)] sm:pt-28 sm:pb-16"
    >
      <div className="pointer-events-none absolute -top-40 left-1/4 -z-10 h-[420px] w-[640px] rounded-full bg-rose-500/[0.06] blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 top-20 -z-10 h-[360px] w-[420px] rounded-full bg-amber-500/[0.05] blur-[120px]" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-wrap items-center gap-2"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Built for clinic owners
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
            Live in 22+ clinics
          </span>
          <span className="hidden text-[11.5px] text-muted-foreground sm:inline">
            Physio · Dental · Chiro · Med-spa · Dietetics · Wellness
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          data-testid="hero-h1"
          className="mt-5 max-w-4xl font-display text-[2.25rem] font-black leading-[1] tracking-[-0.03em] sm:text-[3.25rem] lg:text-[4rem] xl:text-[4.5rem]"
        >
          Run your entire clinic from{" "}
          <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">one screen.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-[16.5px]"
        >
          Plugs into the EMR you already use (Jane, Juvonno, ClinicSense) and{" "}
          <span className="text-foreground/95 font-semibold">answers calls, replies to patients, fills no-show slots, and shows you the numbers</span>
          {" "}— from one screen.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-6 flex flex-col gap-3 sm:flex-row"
        >
          <button
            type="button"
            data-testid="hero-book-demo"
            onClick={openDemo}
            className="group inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-foreground px-6 text-[14px] font-bold text-background shadow-lg shadow-foreground/15 transition-all hover:-translate-y-0.5 hover:shadow-xl sm:text-[14.5px]"
          >
            <CalendarCheck className="h-4 w-4" />
            Book your 20-min demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <Link
            to="/welcome"
            data-testid="hero-self-guided"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-foreground/15 bg-card px-5 text-[14px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30 sm:text-[14.5px]"
          >
            <Sparkles className="h-4 w-4 text-violet-500" />
            Show me how it works
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        {/* --- REDESIGNED BOTTOM BLOCK START --- */}
        
        {/* Trust Row - Upgraded Typography */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          data-testid="hero-trust-row"
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-[14px] font-medium text-foreground/80 sm:text-[15px]"
        >
          <span className="inline-flex items-center gap-1.5 text-foreground/95"><ShieldCheck className="h-4 w-4 text-emerald-500" strokeWidth={2.5} /> HIPAA-ready</span>
          <span className="hidden h-1 w-1 rounded-full bg-border sm:block" />
          <span className="inline-flex items-center gap-1.5 text-foreground/95"><CalendarCheck className="h-4 w-4 text-amber-500" strokeWidth={2.5} /> 14-day go-live</span>
          <span className="hidden h-1 w-1 rounded-full bg-border sm:block" />
          <span className="inline-flex items-center gap-1.5 text-foreground/90"><DollarSign className="h-4 w-4 text-violet-500" strokeWidth={2.5} /> Starts at $297/mo</span>
          <span className="hidden h-1 w-1 rounded-full bg-border sm:block" />
          <span className="inline-flex items-center gap-1.5 text-foreground/90"><Unlock className="h-4 w-4 text-rose-500" strokeWidth={2.5} /> Cancel anytime</span>
        </motion.div>

        {/* 1. Replace the chaos — competitor chips */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.4 }}
          data-testid="hero-replaces"
          className="mt-8"
        >
          <h2
            data-testid="hero-replace-h2"
            className="font-display text-[1.5rem] font-black leading-tight tracking-[-0.025em] sm:text-[1.875rem] lg:text-[2.125rem]"
          >
            Replace{" "}
            <span className="bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 bg-clip-text text-transparent">
              the chaos.
            </span>
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {REPLACED_TOOLS.map((t, i) => <ReplaceChip key={t.name} t={t} i={i} />)}
            <span
              data-testid="replace-many-more"
              className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-foreground/25 bg-card/40 px-3 py-2 text-[12.5px] font-semibold text-muted-foreground"
            >
              <Sparkles className="h-3.5 w-3.5 text-rose-500" />
              and many more
            </span>
          </div>
        </motion.div>

        {/* 2. Live proof — 22+ clinics + big EMR partner cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.5 }}
          data-testid="hero-live-proof"
          className="mt-8"
        >
          <p
            data-testid="hero-live-kicker"
            className="inline-flex items-center gap-2.5 font-serif text-[15px] italic text-foreground/85 sm:text-[16px]"
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="not-italic font-display font-black uppercase tracking-[0.18em] text-foreground/85 text-[12px] sm:text-[12.5px]">
              Connect EMR
            </span>
          </p>
          <div className="mt-4 flex flex-wrap gap-3 sm:gap-4">
            <EmrPartnerCard domain="juvonno.com" label="Juvonno"  to="/integrations/juvonno" />
            <EmrPartnerCard domain="jane.app"    label="Jane App" to="/integrations/jane-app" />
          </div>
        </motion.div>

        {/* 3. Powered by KAI + Meet Clinic OS — compact pill row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="mt-5 flex flex-wrap items-center gap-2"
        >
          <Link
            to="/kai"
            data-testid="hero-kai-pill"
            className="group inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-[13px] shadow-sm transition-all hover:-translate-y-0.5 hover:border-rose-500/35 hover:shadow-md"
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 text-white shadow-sm ring-1 ring-inset ring-white/15">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="font-display font-bold tracking-tight text-foreground/90">
              Powered by{" "}
              <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">KAI</span>
            </span>
            <span className="hidden text-[12.5px] font-medium text-muted-foreground sm:inline">
              · our in-house AI learning platform
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
          </Link>

          <Link
            to="/join-clinicos"
            data-testid="hero-meet-clinicos-pill"
            className="group inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.06] px-3.5 py-1.5 text-[13px] font-display font-bold tracking-tight text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-500/55 hover:bg-emerald-500/15 hover:shadow-md dark:text-emerald-300"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Meet Clinic OS
            <ArrowRight className="h-3.5 w-3.5 transition-all group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
        
        {/* --- REDESIGNED BOTTOM BLOCK END --- */}
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────
   Mini-CTA band between sections.
   ──────────────────────────────────────────────────────────────── */
const CTAStrip = ({ label, note, testid }) => {
  const openDemo = useDemo();
  return (
    <div data-testid={testid} className="border-y border-border bg-secondary/40">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-5 py-6 sm:flex-row sm:justify-between sm:px-8 sm:py-7 lg:px-12">
        <div className="text-center sm:text-left">
          <p className="font-display text-[15px] font-black tracking-tight text-foreground sm:text-[16px]">{label}</p>
          <p className="text-[12px] text-muted-foreground sm:text-[12.5px]">{note}</p>
        </div>
        <button
          type="button"
          onClick={openDemo}
          data-testid={`${testid}-book`}
          className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <CalendarCheck className="h-4 w-4" />
          Book your demo
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────
   Bridge — the positioning shot. EMR ↔ Clinic OS ↔ Patient.
   Sits between Hero and the 5 spotlights as a "here is where we live" beat.
   ──────────────────────────────────────────────────────────────── */
const Bridge = () => {
  const openDemo = useDemo();
  return (
    <section
      data-testid="home-bridge"
      className="relative overflow-hidden border-y border-border bg-secondary/30 py-12 sm:py-16"
    >
      <div className="pointer-events-none absolute -top-20 right-1/4 -z-10 h-72 w-72 rounded-full bg-rose-500/[0.06] blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-20 left-1/4 -z-10 h-72 w-72 rounded-full bg-emerald-500/[0.06] blur-[120px]" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid items-end gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <p className={KICKER}>Where Clinic OS lives</p>
            <h2 className="mt-2 font-display text-[1.875rem] font-black leading-[1.02] tracking-[-0.025em] sm:text-[2.375rem] lg:text-[2.75rem]">
              We sit between your EMR{" "}
              <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">and your patients.</span>
            </h2>
          </div>
          <p className="text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
            Your EMR stays. Your team stays. Clinic OS handles every patient touchpoint in the middle — calls, comms, reviews, recalls, marketing — and writes back to your EMR in real time.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 items-stretch gap-3 sm:grid-cols-[1fr_auto_1.35fr_auto_1fr] sm:gap-2">
          {/* EMR */}
          <div data-testid="bridge-emr" className="rounded-2xl border border-blue-500/30 bg-card p-4 text-center sm:p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm">
              <Stethoscope className="h-4 w-4" />
            </span>
            <p className="mt-2.5 font-display text-[14px] font-black tracking-tight">Your EMR</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Jane · Juvonno · ClinicSense</p>
            <div className="mt-3 flex flex-wrap justify-center gap-1">
              {["Charts","Notes","Availability","Billing"].map((c) => (
                <span key={c} className="rounded-full border border-blue-500/25 bg-blue-500/[0.06] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-blue-700 dark:text-blue-300">{c}</span>
              ))}
            </div>
          </div>

          <BridgeConnector label="2-way sync" />

          {/* CLINIC OS */}
          <div data-testid="bridge-clinic-os" className="relative overflow-hidden rounded-2xl border-2 border-foreground/15 bg-card p-5 text-center shadow-md sm:p-6">
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-rose-500/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-violet-500/15 blur-3xl" />
            <span className="relative inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 text-white shadow-md">
              <Sparkles className="h-5 w-5" />
            </span>
            <p className="relative mt-2.5 font-display text-[17px] font-black tracking-tight">Clinic OS</p>
            <p className="relative mt-1 text-[11.5px] text-muted-foreground">Inbox · calls · AI · reviews · recalls</p>
            <div className="relative mt-3 grid grid-cols-2 gap-1 sm:grid-cols-3">
              {[
                { Ic: Phone,         label: "AI Receptionist" },
                { Ic: Inbox,         label: "Unified Inbox" },
                { Ic: BookOpen,      label: "Ask KAI · Wiki" },
                { Ic: BarChart3,     label: "Clinic Pulse" },
                { Ic: Megaphone,     label: "Reactivation" },
                { Ic: Star,          label: "Review AI" },
              ].map((m) => {
                const Ic = m.Ic;
                return (
                  <span key={m.label} className="inline-flex items-center justify-center gap-1 rounded-md border border-border bg-background/60 px-1.5 py-1 text-[10px] font-semibold text-foreground/85">
                    <Ic className="h-2.5 w-2.5 text-rose-500" />
                    {m.label}
                  </span>
                );
              })}
            </div>
          </div>

          <BridgeConnector label="every channel" />

          {/* PATIENTS */}
          <div data-testid="bridge-patient" className="rounded-2xl border border-emerald-500/30 bg-card p-4 text-center sm:p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
              <Users className="h-4 w-4" />
            </span>
            <p className="mt-2.5 font-display text-[14px] font-black tracking-tight">Your patients</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Wherever they reach you</p>
            <div className="mt-3 flex flex-wrap justify-center gap-1">
              {["Calls","SMS","Email","DMs","Reviews","Walk-ins"].map((c) => (
                <span key={c} className="rounded-full border border-emerald-500/25 bg-emerald-500/[0.06] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">{c}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Outcomes proof — 5 stats across 3+2 rows */}
        {(() => {
          const OUTCOMES = [
            {
              Ic: Phone,
              tone: "from-rose-500 to-orange-500",
              value: "99%",
              label: "of calls answered",
              sub: "Industry: 62% · KAI works after hours too",
            },
            {
              Ic: BarChart3,
              tone: "from-amber-500 to-rose-500",
              value: "11 hrs",
              label: "back to your front desk weekly",
              sub: "Out of inboxes, onto patients in the chair",
            },
            {
              Ic: Megaphone,
              tone: "from-emerald-500 to-teal-500",
              value: "$8.4k",
              label: "revenue recovered every month",
              sub: "From no-shows nudged + dormant leads re-booked",
            },
            {
              Ic: Star,
              tone: "from-violet-500 to-fuchsia-500",
              value: "98%",
              label: "of reviews replied to",
              sub: "Across Google, Facebook + Yelp · in your tone",
            },
            {
              Ic: Inbox,
              tone: "from-blue-500 to-sky-500",
              value: "< 60s",
              label: "first reply on every channel",
              sub: "Industry: ~8 hrs · KAI drafts, your team approves",
            },
          ];
          const renderCard = (o, i) => {
            const Ic = o.Ic;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`bridge-outcome-${i}`}
                className="relative overflow-hidden rounded-2xl border border-border bg-card px-4 py-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:px-5"
              >
                <div className={`pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br opacity-25 blur-2xl ${o.tone}`} />
                <div className="relative flex items-start gap-3">
                  <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${o.tone} text-white shadow-sm`}>
                    <Ic className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className={`font-display text-[1.5rem] font-black leading-none tracking-[-0.025em] bg-gradient-to-br ${o.tone} bg-clip-text text-transparent sm:text-[1.75rem]`}>
                      {o.value}
                    </p>
                    <p className="mt-1 text-[12.5px] font-bold leading-tight text-foreground/90 sm:text-[13px]">
                      {o.label}
                    </p>
                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                      {o.sub}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          };

          return (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45 }}
              data-testid="bridge-outcomes"
              className="mt-8 space-y-3 sm:space-y-4"
            >
              {/* Row 1 — 3 cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
                {OUTCOMES.slice(0, 3).map(renderCard)}
              </div>
              {/* Row 2 — 2 cards centered */}
              <div className="mx-auto grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                {OUTCOMES.slice(3, 5).map((o, i) => renderCard(o, i + 3))}
              </div>
            </motion.div>
          );
        })()}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={openDemo}
            data-testid="bridge-cta-demo"
            className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <CalendarCheck className="h-4 w-4" />
            See where we plug in
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <Link
            to="/integrations"
            data-testid="bridge-cta-integrations"
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-foreground/15 bg-card px-4 text-[13px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
          >
            Browse EMR &amp; tool integrations
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const BridgeConnector = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-1 sm:px-1">
    <div className="hidden sm:block relative h-px w-10 overflow-hidden bg-border">
      <motion.span
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-foreground/40 to-transparent"
      />
    </div>
    {label && (
      <span className="hidden sm:inline-block text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground/80">
        {label}
      </span>
    )}
    <Plug className="block sm:hidden h-4 w-4 text-muted-foreground/60" />
  </div>
);

/* ────────────────────────────────────────────────────────────────
   Closing CTA — sits after the 5 spotlights, before ChannelHub.
   ──────────────────────────────────────────────────────────────── */
const ClosingCTA = () => {
  const openDemo = useDemo();
  return (
    <section data-testid="home-closing-cta" className="relative overflow-hidden border-y border-border py-14 sm:py-20">
      <div className="pointer-events-none absolute -top-32 left-1/3 -z-10 h-96 w-96 rounded-full bg-amber-500/[0.07] blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-32 right-1/4 -z-10 h-96 w-96 rounded-full bg-rose-500/[0.06] blur-[140px]" />

      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <p className={KICKER}>Ready when you are</p>
        <h2 className="mt-3 font-display text-[2rem] font-black leading-[1.02] tracking-[-0.025em] sm:text-[2.75rem] lg:text-[3.25rem]">
          See it running on{" "}
          <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">
            your numbers.
          </span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[14.5px] leading-relaxed text-muted-foreground sm:text-[15.5px]">
          A 20-minute walkthrough on your specialty, your EMR and your stack. You leave with a clear shortlist and a sizing — whether Clinic OS is the right fit or not.
        </p>

        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            data-testid="home-closing-book"
            onClick={openDemo}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-7 text-[14px] font-bold text-background shadow-lg shadow-foreground/15 transition-all hover:-translate-y-0.5 hover:shadow-xl sm:text-[14.5px]"
          >
            <CalendarCheck className="h-4 w-4" />
            Book your 20-min demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <Link
            to="/join-clinicos"
            data-testid="home-closing-join"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-foreground/15 bg-card px-6 text-[14px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30 sm:text-[14.5px]"
          >
            Read the full story
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-emerald-500" /> HIPAA-ready</span>
          <span className="h-1 w-1 rounded-full bg-border" />
          <span>Live with 22+ clinics</span>
          <span className="h-1 w-1 rounded-full bg-border" />
          <span>14-day go-live · Starts at $297/mo</span>
        </p>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────
   How it works — 3 calm steps.
   ──────────────────────────────────────────────────────────────── */
const HowItWorks = () => {
  const openDemo = useDemo();
  const steps = [
    { n: "01", title: "20-min discovery call.", body: "We learn your stack, your specialty, your goals. No deck. No pitch. You leave with a clear shortlist — even if Clinic OS isn&rsquo;t the right fit." },
    { n: "02", title: "We plug into your EMR.", body: "Jane, Juvonno, ClinicSense — 2-way sync goes live. Your team keeps charting where they already do. We migrate the rest." },
    { n: "03", title: "You go live in 14 days.", body: "AI Receptionist answers calls. Inbox unified. Owner dashboard live. We sit with you weekly for 60 days, then hand over the keys." },
  ];

  return (
    <section data-testid="home-how-it-works" className="relative bg-secondary/40 py-12 sm:py-16">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid items-start gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          <div>
            <p className={KICKER}>From hello to live</p>
            <h2 className="mt-2 font-display text-[1.875rem] font-black leading-[1] tracking-[-0.03em] sm:text-[2.25rem] lg:text-[2.75rem]">
              Three steps. <span className="text-muted-foreground">No surprises.</span>
            </h2>
            <p className="mt-4 max-w-md text-[14px] leading-relaxed text-muted-foreground">
              We have walked 22+ clinics through this. Same calm pace, same playbook, same outcomes.
            </p>
            <button
              type="button"
              onClick={openDemo}
              data-testid="how-book-demo"
              className="mt-5 group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5"
            >
              <CalendarCheck className="h-4 w-4" />
              Start with step 1
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          <ol className="relative space-y-8 border-l border-border pl-7 sm:space-y-10 sm:pl-10">
            {steps.map((s) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, x: 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4 }}
                data-testid={`how-step-${s.n}`}
                className="relative"
              >
                <span className="absolute -left-[36px] inline-flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card font-display text-[11px] font-bold text-foreground/75 sm:-left-[50px] sm:h-10 sm:w-10 sm:text-[13px]">
                  {s.n}
                </span>
                <h3 className="font-display text-[1.5rem] font-black leading-tight tracking-[-0.02em] sm:text-[1.875rem]">
                  {s.title}
                </h3>
                <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]" dangerouslySetInnerHTML={{ __html: s.body }} />
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────
   PAGE COMPOSITION
   Concrete feature spotlights — Call IQ, Ask KAI, Pulse, Reactivation
   plus the "every channel, one inbox" hub.
   ──────────────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <>
      <Hero />

      {/* Positioning beat — where Clinic OS lives */}
      <Bridge />

      {/* ──── 1. Call IQ + KAI Receptionist ──── */}
      <FeatureSpotlight
        id="call-iq"
        testid="spotlight-call-iq"
        accent="rose"
        side="right"
        tag="Call IQ · powered by KAI"
        title="Every missed call is a patient you never see again."
        highlight="Fix that in 14 days."
        lead="Your physical desk phone, plugged into Clinic OS. KAI picks up after 3 rings on busy days and 24/7 after hours — books, reschedules, cancels. Your team only hears the calls that need a human."
        bullets={[
          "Connects to your existing desk phone — not another mobile app",
          "Every call recorded, transcribed, and tagged by the agent (AI or human) who spoke",
          "AI Receptionist pulls instant availability from your EMR before quoting a slot",
          "Books, reschedules and cancels appointments — all in your EMR, in real time",
        ]}
        stat={{ value: "99%", label: "Of calls answered — after hours, on lunch, on snow days." }}
        cta={{ label: "Hear KAI on a real call", action: "demo", secondary: { label: "Read the Call IQ page", to: "/products/call-iq-ai" } }}
        visual={<CallIQVisual />}
      />

      {/* ──── 3. Ask KAI — knowledge base / wiki ──── */}
      <FeatureSpotlight
        id="ask-kai"
        testid="spotlight-ask-kai"
        accent="violet"
        side="left"
        tag="Ask KAI · Internal wiki"
        title="Your clinic answers the same way."
        highlight="Every shift. Every channel. Every year."
        lead="Drop your SOPs, policies, intake scripts and FAQs into Clinic OS — KAI turns them into a living wiki your whole clinic queries. The same wiki answers patients on calls, SMS, email, reviews, website chat — and your team in Slack."
        bullets={[
          "One wiki · upload PDFs, Google Docs, Notion exports — versioned with audit log",
          "Patients get the same correct answer across every channel they reach out on",
          "Your team queries it directly — onboarding day 1 to year 5, same source of truth",
          "Updates roll out instantly · revert to any prior policy in two clicks",
        ]}
        stat={{ value: "1 wiki", label: "One knowledge base — answering patients and your team alike." }}
        cta={{ label: "See the wiki on a demo", action: "demo", secondary: { label: "Read the KAI page", to: "/kai" } }}
        visual={<AskKaiVisual />}
      />

      {/* ──── 4. Clinic Pulse ──── */}
      <FeatureSpotlight
        id="clinic-pulse"
        testid="spotlight-clinic-pulse"
        accent="emerald"
        side="right"
        tag="Clinic Pulse · Owner dashboards"
        title="The numbers most owners only see weekly."
        highlight="Live in Clinic Pulse."
        lead="Revenue, clinician utilisation, no-show rate, recall conversion, AI activity — all rolled into one live dashboard and a Monday-morning email digest. Every number traces back to its source row in your EMR. Audit-grade for insurers, finance, and your board."
        bullets={[
          "Live dashboard + weekly email digest delivered to the owner — automatically",
          "Full clinician utilisation by therapist, location, and program",
          "Drill into any number → see source EMR row + timestamp in two clicks",
          "Audit-grade exports for insurers, finance and compliance",
        ]}
        stat={{ value: "live", label: "Revenue visibility — compared to weekly EMR exports nobody opens." }}
        cta={{ label: "See your numbers on a demo", action: "demo", secondary: { label: "Read about Clinic Pulse", to: "/juvonno/clinicpulse" } }}
        visual={<ClinicPulseVisual />}
      />

      {/* ──── 5. Reactivation Engine ──── */}
      <FeatureSpotlight
        id="reactivation"
        testid="spotlight-reactivation"
        accent="amber"
        side="left"
        tag="Reactivation engine"
        title="Patients you forgot existed."
        highlight="Come back this month."
        lead="Stop building funnels in Mailchimp at midnight. Clinic OS auto-builds your service-tips, clinic newsletter and reactivation campaigns from your EMR data — and pushes them across SMS, email and call in the right order."
        bullets={[
          "Reactivation flows auto-target patients gone quiet (90 / 180 / 365 days)",
          "Service-tip campaigns by program — physio, dental, wellness, etc.",
          "Owner-approved monthly newsletter, drafted by KAI in your voice",
          "Multi-channel sequencing · SMS → email → human call · all built-in",
        ]}
        stat={{ value: "55%", label: "Recall cycle conversion — vs. industry average around 30%." }}
        cta={{ label: "See reactivation flows", action: "demo", secondary: { label: "Read about Sales Autopilot", to: "/products/sales-autopilot" } }}
        visual={<ReactivationVisual />}
      />

      {/* Closing CTA after the 5 spotlights */}
      <ClosingCTA />

      {/* Architectural recap */}
      <ChannelHub />

      <HowItWorks />
      <FinalCTA />
    </>
  );
}
